// Ficha de negocio: barra de etapas clicable (cambia la etapa), reabrir, y crear
// cotización desde el negocio.
import type { JSX } from "react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api.ts";
import { usePipelines, type Stage } from "../lib/lookups.ts";
import type { Row } from "../lib/resource.ts";
import { Button, Card, ErrorState, Field, Loading, Modal, Select } from "../components/ui.tsx";
import { toast } from "../components/toast.tsx";
import { Paperclip, Trash2 } from "lucide-react";

const LOSS_REASONS = ["Precio", "Competencia", "Sin presupuesto", "Sin respuesta", "Tiempo", "Otro"] as const;

export function DealDetail(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: pipelines } = usePipelines();

  const deal = useQuery<{ deal: Row; access: string }>({
    queryKey: ["record", "deals", id],
    enabled: Boolean(id),
    queryFn: () => api.get(`/deals/${id}`),
  });

  const [lossFor, setLossFor] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);

  const patch = useMutation({
    mutationFn: (body: Row) => api.patch(`/deals/${id}`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["record", "deals", id] }); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo actualizar."),
  });
  const reopen = useMutation({
    mutationFn: () => api.post(`/deals/${id}/reopen`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["record", "deals", id] }); toast.success("Negocio reabierto."); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo reabrir."),
  });
  const createQuote = useMutation({
    mutationFn: () => api.post<{ quote: Row }>(`/deals/${id}/quotes`),
    onSuccess: (r) => { toast.success("Cotización creada."); navigate(`/quotes/${String(r.quote["id"])}`); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo crear la cotización."),
  });

  if (deal.isLoading) return <Loading />;
  if (deal.isError || !deal.data) return <ErrorState message="No se encontró el negocio." />;
  const d = deal.data.deal;

  const pipeline = (pipelines?.pipelines ?? []).find((p) => p.id === String(d["pipelineId"]));
  const currentStageId = String(d["stageId"]);
  const currentStage = pipeline?.stages.find((s) => s.id === currentStageId);
  const isClosed = currentStage?.stageType !== "open" && currentStage !== undefined;

  // La barra de etapas NAVEGA entre subapartados (no cambia la etapa al clic).
  const viewStageId = (picked && pipeline?.stages.some((s) => s.id === picked)) ? picked : currentStageId;
  const viewStage = pipeline?.stages.find((s) => s.id === viewStageId);
  const isCheckView = viewStage?.stageType === "closed_won"; // la pestaña "Check"

  const changeStage = (st: Stage): void => {
    if (st.id === currentStageId) return;
    if (st.stageType === "closed_lost") { setLossFor(st.id); return; }
    patch.mutate({ stageId: st.id });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button className="btn-ghost btn-sm" onClick={() => navigate("/deals")}>←</button>
          <h1 className="text-xl font-bold text-ink">{String(d["dealName"])}</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => createQuote.mutate()}>Nueva cotización</Button>
          {isClosed && <Button variant="primary" onClick={() => reopen.mutate()}>Reabrir negocio</Button>}
        </div>
      </div>

      {/* Barra de etapas: cada pestaña es un subapartado independiente. Al hacer clic
          se NAVEGA a su subapartado; la etapa actual se marca con ●. */}
      {pipeline && (
        <Card className="p-2">
          <div className="flex gap-1 overflow-x-auto">
            {pipeline.stages.map((st) => {
              const active = st.id === viewStageId;
              const isCurrent = st.id === currentStageId;
              return (
                <button key={st.id} onClick={() => setPicked(st.id)}
                  className={`flex-1 min-w-28 text-xs py-2 px-2 rounded ${active ? "text-white font-medium" : "bg-surface-hover text-ink-secondary hover:bg-surface"}`}
                  style={active ? { backgroundColor: st.color } : {}}>
                  {isCurrent ? "● " : ""}{st.name}
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Subapartado de la pestaña activa: solo "Check" tiene contenido; el resto en blanco. */}
      {viewStage && (
        viewStageId !== currentStageId && (
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => changeStage(viewStage)} disabled={patch.isPending}>
              Mover negocio a «{viewStage.name}»
            </Button>
          </div>
        )
      )}

      {isCheckView ? (
        <CheckNodesCard dealId={String(id)} deal={d} />
      ) : (
        <Card className="p-8 text-center text-sm text-ink-tertiary">
          {viewStage ? `«${viewStage.name}» — sin contenido.` : ""}
        </Card>
      )}

      {lossFor && (
        <LossReasonModal onClose={() => setLossFor(null)} onConfirm={(reason) => { patch.mutate({ stageId: lossFor, lossReason: reason }); setLossFor(null); }} />
      )}
    </div>
  );
}

interface CheckNode { id: string; name: string; done: boolean; weight: number; clip?: boolean }

function CheckNodesCard({ dealId, deal }: { dealId: string; deal: Row }): JSX.Element {
  const qc = useQueryClient();
  const cd = (deal["customData"] as Record<string, unknown> | undefined) ?? {};
  const check = (cd["check"] as { weightMode?: string; nodes?: CheckNode[] } | undefined) ?? {};
  const mode: "equal" | "manual" = check.weightMode === "manual" ? "manual" : "equal";
  const nodes: CheckNode[] = Array.isArray(check.nodes) ? check.nodes : [];

  const [newName, setNewName] = useState("");

  const save = useMutation({
    mutationFn: (next: { weightMode: string; nodes: CheckNode[] }) => api.patch(`/deals/${dealId}`, { customData: { check: next } }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["record", "deals", dealId] }); },
    onError: () => toast.error("No se pudo guardar."),
  });
  const persist = (m: string, ns: CheckNode[]): void => { save.mutate({ weightMode: m, nodes: ns }); };
  const addNode = (): void => { const t = newName.trim(); if (!t) return; persist(mode, [...nodes, { id: crypto.randomUUID(), name: t, done: false, weight: 0 }]); setNewName(""); };
  const toggleClip = (nid: string): void => persist(mode, nodes.map((n) => (n.id === nid ? { ...n, clip: !n.clip } : n)));
  const setWeight = (nid: string, w: number): void => persist(mode, nodes.map((n) => (n.id === nid ? { ...n, weight: w } : n)));
  const rename = (nid: string, name: string): void => persist(mode, nodes.map((n) => (n.id === nid ? { ...n, name } : n)));
  const removeNode = (nid: string): void => { persist(mode, nodes.filter((n) => n.id !== nid)); };

  const eff = (n: CheckNode): number => (mode === "equal" ? (nodes.length ? 100 / nodes.length : 0) : (Number(n.weight) || 0));
  const totalManual = nodes.reduce((s, n) => s + (Number(n.weight) || 0), 0);
  const over = mode === "manual" && totalManual > 100;

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h3 className="text-sm font-semibold text-ink">Check — Nodos del negocio</h3>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-ink-muted mr-1">Ponderación:</span>
          <button onClick={() => persist("equal", nodes)} className={`px-2 py-1 rounded ${mode === "equal" ? "bg-primary text-white" : "bg-surface-hover text-ink-secondary"}`}>Equitativa</button>
          <button onClick={() => persist("manual", nodes)} className={`px-2 py-1 rounded ${mode === "manual" ? "bg-primary text-white" : "bg-surface-hover text-ink-secondary"}`}>Manual %</button>
        </div>
      </div>

      {mode === "manual" && (
        <p className={`text-xs mb-3 ${over ? "text-red-500 font-medium" : "text-ink-muted"}`}>Suma de porcentajes: {totalManual}%{over ? " · ⚠️ no debe superar 100%" : ` · disponible ${Math.max(0, 100 - totalManual)}%`}</p>
      )}

      {nodes.length === 0 ? <p className="text-sm text-ink-muted my-3">Aún no hay nodos. Agrega uno para empezar.</p> : (
        <ul className="space-y-2 my-3">
          {nodes.map((n) => (
            <li key={n.id} className="border border-border rounded-lg p-2.5">
              <div className="flex items-center gap-2">
                <input defaultValue={n.name} onBlur={(e) => { if (e.target.value !== n.name) rename(n.id, e.target.value); }}
                  className="flex-1 bg-transparent text-sm outline-none text-ink" />
                {mode === "manual" ? (
                  <span className="flex items-center gap-1"><input type="number" min={0} max={100} defaultValue={n.weight || 0}
                    onBlur={(e) => setWeight(n.id, Number(e.target.value))} className="w-14 input text-sm text-right py-1" /><span className="text-sm font-semibold text-ink">%</span></span>
                ) : (
                  <span className="text-sm font-semibold text-ink w-12 text-right">{eff(n).toFixed(0)}%</span>
                )}
                {/* Clip: solo marca el nodo en azul (sin adjuntar archivos). */}
                <button onClick={() => toggleClip(n.id)} title={n.clip ? "Quitar marca" : "Marcar"}
                  className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 transition-colors ${n.clip ? "bg-primary text-white shadow-sm" : "text-ink-muted hover:bg-surface-hover hover:text-primary"}`}>
                  <Paperclip size={15} strokeWidth={2} />
                </button>
                <button onClick={() => removeNode(n.id)} title="Eliminar nodo" className="btn-ghost btn-sm text-ink-muted hover:text-red-500"><Trash2 size={15} strokeWidth={1.75} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNode(); }} placeholder="Nombre del nodo…" className="input flex-1 text-sm" />
        <Button small onClick={addNode} disabled={!newName.trim()}>+ Nodo</Button>
      </div>
    </Card>
  );
}

function LossReasonModal({ onClose, onConfirm }: { onClose: () => void; onConfirm: (r: string) => void }): JSX.Element {
  const [reason, setReason] = useState<string>(LOSS_REASONS[0]);
  return (
    <Modal open onClose={onClose} title="Marcar como perdido"
      footer={<><Button onClick={onClose}>Cancelar</Button><Button variant="danger" onClick={() => onConfirm(reason)}>Marcar perdido</Button></>}>
      <Field label="Motivo de la pérdida" required>
        <Select value={reason} onChange={(e) => setReason(e.target.value)}>
          {LOSS_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </Select>
      </Field>
    </Modal>
  );
}
